import React, { useState } from 'react';
import { Button, Col, Input, List, notification, Popconfirm, Row } from 'antd';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { TenantInterface } from '@app/modules/Tenants/Tenant.interfaces';
import { Can } from '@app/modules/Auth';
import { CheckOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useStores } from '@app/hooks/stores';
import api from '../api';
import ProfilesSelect from './ProfilesSelect';

declare type TenantItemProps = {
  item: TenantInterface;
};

const TenantItem: React.FC<TenantItemProps> = ({ item }) => {
  const { tenantsStore } = useStores();

  const [editingItemsMap, setEditingMode] = useState<{ [key: number]: boolean }>({});
  const [deleteConfirmationsMap, setDeleteConfirmationVisibility] = useState<{ [key: number]: boolean }>({});

  const isSelected = tenantsStore.selectedTenant?.id === item.id;

  const toggleEditingOnItem = (item, forcedValue) => {
    setEditingMode({
      ...editingItemsMap,
      ...{
        [item.id]: typeof forcedValue !== 'undefined' ? forcedValue : !editingItemsMap[item.id]
      }
    });
  };

  const onEditMode = (item: TenantInterface) => {
    tenantsStore.saveBackupTenant(item);
    toggleEditingOnItem(item, true);
  };

  const onCancelEditing = (item: TenantInterface) => {
    if (item.id) {
      tenantsStore.restoreBackedUpTenant(item);
    } else {
      // remove blank item
      tenantsStore.removeTenant(item);
    }
    toggleEditingOnItem(item, false);
  };

  const isEditing = !item.id || editingItemsMap[item.id];

  const selectItem = () => {
    tenantsStore.setSelectedTenant(item);
  };

  const onChangeName = (item: TenantInterface, event) => {
    const name = event.target.value;
    item.name = name;
  };

  const onSetProfile = profileId => {
    item.profileId = profileId;
  };

  const onSave = async (item: TenantInterface) => {
    try {
      if (item.id) {
        const { id, ...body } = item;
        // saving
        await api.editTenant(id, body);
        notification.success({
          message: 'Tenant saved!'
        });
      } else {
        if (!item.name) {
          notification.error({
            message: 'Tenant name should not be empty!'
          });
          return;
        }
        const id = await api.addTenant(toJS(item));
        item.id = id;
        notification.success({
          message: 'Tenant added successfully!'
        });
      }
    } catch (err) {
      debugger;
    }

    toggleEditingOnItem(item, false);
  };

  const handleDeleteConfirmationVisibility = (visible, item: TenantInterface) => {
    setDeleteConfirmationVisibility({
      ...deleteConfirmationsMap,
      ...{
        [item.id]: visible
      }
    });
  };

  const onDelete = async (item: TenantInterface) => {
    try {
      await api.deleteTenant(item.id);
      tenantsStore.removeTenant(item);
      notification.success({
        message: `Tenant "${item.name}" deleted !`
      });
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Error while deleting tenant:\n ${e.message}`
      });
    }
  };

  return (
    <List.Item
      style={{ cursor: 'pointer' }}
      onClick={selectItem}
      key={item.id}
      actions={
        isEditing
          ? [
              <Can
                action="put:tenants"
                yes={() => <Button type="link" icon={<CheckOutlined />} onClick={e => onSave(item)} />}
                no={() => <Button type="link" icon={<CheckOutlined />} disabled />}
              />,
              <Button type="link" icon={<StopOutlined />} onClick={e => onCancelEditing(item)} />
            ]
          : [
              <Can
                action="put:tenants"
                yes={() => <Button type="link" onClick={e => onEditMode(item)} icon={<EditOutlined />} />}
                no={() => <Button type="link" disabled icon={<EditOutlined />} />}
              />,
              <Can
                action="delete:tenants"
                yes={() => (
                  <Popconfirm
                    title={`Are you sure you want to delete "${item.name}" tenant?`}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    open={deleteConfirmationsMap[item.id]}
                    onOpenChange={visible => handleDeleteConfirmationVisibility(visible, item)}
                    onConfirm={() => onDelete(item)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
                no={() => <Button type="link" disabled icon={<DeleteOutlined />} />}
              />
            ]
      }
    >
      {isEditing && item.id && (
        <Row gutter={[24, 16]}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Input
              value={item.name}
              size="middle"
              onChange={value => {
                onChangeName(item, value);
              }}
              autoFocus
              placeholder="Name"
            />
          </Col>
          <Col lg={16} md={16} sm={24} xs={24}>
            <ProfilesSelect onChange={onSetProfile} disabled={!isEditing} placeholder="Apply profile" />
            <div>
              <small>also set profile for all rooms</small>
            </div>
          </Col>
        </Row>
      )}
      {isEditing && !item.id && (
        <Input
          value={item.name}
          size="small"
          onChange={value => {
            onChangeName(item, value);
          }}
          autoFocus
          placeholder="Name"
        />
      )}
      {!isEditing &&
        (isSelected ? <List.Item.Meta title={<strong>{item.name}</strong>} /> : <List.Item.Meta title={item.name} />)}
    </List.Item>
  );
};

export default observer(TenantItem);
